import type { EstimateDetail, Tuple } from "@/interfaces/estimate-detail";

export interface MergedPageLookup {
  currentPageIndex: number;
  tuples: Tuple[];
  tupleIndex: number;
  resolvedPageId: string;
}

/**
 *
 * Finds a tuple within estimation detail, handling merged pages.
 *
 * Pages can be merged (one page's `mergeWith` references another page's ID).
 * If the tuple isn't found in the target page, this checks merged pages.
 *
 * @param state - The estimation detail state
 * @param pageId - The page ID
 * @param tupleId - The tuple ID
 * @returns The merged page lookup
 */
export function getDetailFromPossiblyMergedPage(
  state: EstimateDetail[],
  pageId: string,
  tupleId: string
): MergedPageLookup {
  let currentPageIndex = state.findIndex((detail) => detail.dataForUI.metadata.pageId === pageId);
  let tuples = state?.[currentPageIndex]?.dataForUI.page.tuplesOutput || [];
  let tupleIndex = tuples.findIndex((tuple) => tuple.tupleId === tupleId);
  let resolvedPageId = pageId;

  // If we can't find the tuple, check merged pages
  if (tupleIndex === -1) {
    const mergedPage = state.find((page) => page.dataForUI.page.mergeWith === pageId);
    if (mergedPage) {
      tuples = mergedPage.dataForUI.page.tuplesOutput;
      tupleIndex = tuples.findIndex((tuple) => tuple.tupleId === tupleId);
      resolvedPageId = mergedPage.dataForUI.metadata.pageId;
      currentPageIndex = state.findIndex((detail) => detail.dataForUI.metadata.pageId === resolvedPageId);
    }
  }

  return { currentPageIndex, tuples, tupleIndex, resolvedPageId };
}
